"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/pelayanan/ - tempat berkas INI
   berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./pelayanan                   -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { ALAMAT, FOTO, NOMOR, PESAN_WA, SUREL } from "./pelayanan";

/* ============================================================
   PELAYANAN KESEHATAN - /layanan/pelayanan

   Sebelum berkas ini ada, alamat ini jatuh ke halaman 404 "Sedang
   Dalam Proses Pengembangan" - padahal sudah ditunjuk menu Header.

   ============================================================
   HALAMAN LAMA HANYA MENUMPUK NOMOR. INI TIDAK.

   Versi lama menampilkan dua nomor sebagai teks biasa, lalu
   menyembunyikan tiga lainnya di kaki halaman. Lima nomor tanpa
   penjelasan apa pun soal MANA UNTUK APA.

   Akibatnya orang menelepon nomor pertama yang terlihat, lalu
   dialihkan - kadang dua kali. Yang paling dirugikan justru yang
   paling butuh: peserta BPJS dan orang yang ingin mengadu.

   Jadi tiap nomor di halaman ini punya satu kalimat keterangan.
   Bukan hiasan - itulah yang membuat halaman ini berguna
   dibandingkan sekadar daftar angka.

   ============================================================
   KENAPA TIDAK ADA "IGD 24 JAM" DI SINI

   Godaannya besar, karena nomor gawat darurat memang yang paling
   penting di situs rumah sakit. Tapi halaman ini bukan tempatnya:
   orang yang sedang gawat darurat tidak menelusuri menu Fasilitas &
   Layanan.

   Halaman IGD sudah ada dan sudah memuatnya. Menduplikasi di sini
   berarti dua tempat yang harus sama-sama diperbarui - dan
   biasanya hanya satu yang teringat.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah
   beberapa baris ikut membawa \r\n ke dalam nilai atribut dan
   memicu hydration error. Lihat catatan di Facility.jsx.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

const TAUTKAN_INDUK = true;

/* ============================================================
   TANDA "PERLU DICEK" PADA NOMOR

   false = nomor bertanda `perluDicek` ditampilkan biasa saja.
   true  = diberi tanda kecil, HANYA saat pengembangan.

   Sengaja default false. Tandanya berguna bagi yang mengerjakan
   situs, tapi pengunjung yang melihat "nomor ini belum
   dipastikan" justru jadi ragu menelepon - padahal nomornya
   kemungkinan besar benar.

   Nyalakan saat sedang membereskan data nomor, matikan lagi
   setelahnya. Di produksi ia tidak pernah tampil, apa pun
   nilainya.
   ============================================================ */
const TANDAI_PERLU_DICEK = false;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Pelayanan Kesehatan", en: "Health Services" },
  sub: {
    id: "Hubungi nomor di bawah ini untuk informasi layanan kesehatan RS Jakarta Medical Center. Setiap nomor melayani keperluan yang berbeda - pilih yang sesuai supaya tidak perlu dialihkan.",
    en: "Contact the numbers below for information about health services at Jakarta Medical Center Hospital. Each number handles a different need - choosing the right one saves you being transferred.",
  },

  nomorJudul: { id: "Nomor yang Dapat Dihubungi", en: "Who to Contact" },
  chatWa: { id: "Chat WhatsApp", en: "Chat on WhatsApp" },
  telepon: { id: "Telepon", en: "Call" },
  perluDicek: { id: "Perlu dipastikan", en: "Needs confirming" },

  lokasiJudul: { id: "Lokasi dan Surel", en: "Location and Email" },
  alamat: { id: "Alamat", en: "Address" },
  surel: { id: "Surel", en: "Email" },
  lihatPeta: { id: "Buka di Google Maps", en: "Open in Google Maps" },

  fotoJudul: { id: "Fasilitas Pelayanan", en: "Service Areas" },
  fotoSub: { id: "Klik untuk memperbesar.", en: "Click to enlarge." },
  perbesar: { id: "Perbesar foto", en: "Enlarge photo" },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Sebelumnya", en: "Previous" },
  berikutnya: { id: "Berikutnya", en: "Next" },
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

export default function PelayananContent() {
  const { lang, t } = useLanguage();
  const [pratinjau, setPratinjau] = useState(null);

  const utama = NOMOR.find((n) => n.utama) ?? NOMOR[0];
  const lainnya = NOMOR.filter((n) => !n.utama);

  const peta = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ALAMAT.cari)}`;

  /* Nomor dengan medan `wa` membuka WhatsApp beserta pesan pembuka
     yang sudah terisi; sisanya membuka aplikasi telepon. */
  function tautanNomor(n) {
    if (!n.wa) return `tel:${n.tel}`;
    const pesan = pilihTeks(n.pesanWa ?? PESAN_WA, lang);
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pesan)}`;
  }

  const tampilkanTanda =
    TANDAI_PERLU_DICEK && process.env.NODE_ENV !== "production";

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}>
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA: DUA KOLOM ----------
         Teks di kiri, nomor utama sebagai kartu besar di kanan.
         Di layar sempit keduanya menumpuk sendiri.

         Nomor utama sengaja dipisah dari daftar di bawah dan dibuat
         sebesar ini: delapan dari sepuluh orang yang membuka halaman
         ini hanya butuh nomor Customer Service. Menyembunyikannya di
         antara empat nomor lain membuat mayoritas mengerjakan
         pekerjaan yang seharusnya tidak perlu. */}
      <div className="bg-[var(--c-tint)]">
        <div style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "48px 16px 48px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 40, alignItems: "center" }}>

            <div style={{ minWidth: 0 }}>
              <p className="font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]" style={{ fontSize: 11.5 }}>
                {pilihTeks(TEKS.eyebrow, lang)}
              </p>

              <h1 className="text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ marginTop: 10, fontSize: "clamp(30px, 4.4vw, 44px)" }}>
                {pilihTeks(TEKS.judul, lang)}
              </h1>

              <p className="text-[var(--c-body)]" style={{ marginTop: 16, maxWidth: 520, fontSize: 15.5, lineHeight: 1.7 }}>
                {pilihTeks(TEKS.sub, lang)}
              </p>
            </div>

            <div className="bg-[var(--c-surface)] shadow-sm" style={{ borderRadius: 18, padding: "26px 24px" }}>
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                {pilihTeks(utama.label, lang)}
              </p>

              {/* Nomornya sendiri yang jadi tulisan tombol, bukan kata
                 "Hubungi kami". Orang yang membuka dari komputer perlu
                 MEMBACA angkanya untuk ditekan di ponsel lain. */}
              <p style={{ marginTop: 10 }}>
                <a
                  href={`tel:${utama.tel}`}
                  className="font-bold leading-none text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]"
                  style={{ fontSize: "clamp(26px, 3.6vw, 34px)" }}
                >
                  {utama.tampil}
                </a>
              </p>

              {utama.keterangan && (
                <p className="text-[var(--c-body)]" style={{ marginTop: 12, fontSize: 14.5, lineHeight: 1.65 }}>
                  {pilihTeks(utama.keterangan, lang)}
                </p>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
                {utama.wa && (
                  <a
                    href={tautanNomor(utama)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 rounded-[12px] bg-[var(--c-brand)] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)]"
                    style={{ padding: "13px 20px", fontSize: 14.5 }}
                  >
                    <IkonWhatsApp />
                    {pilihTeks(TEKS.chatWa, lang)}
                  </a>
                )}

                <a
                  href={`tel:${utama.tel}`}
                  className="inline-flex items-center gap-2 rounded-[12px] border border-[var(--c-brand-line)] bg-[var(--c-surface)] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint-2)]"
                  style={{ padding: "13px 18px", fontSize: 14.5 }}
                >
                  <IkonTelepon />
                  {pilihTeks(TEKS.telepon, lang)}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section style={{ maxWidth: LEBAR_HALAMAN, margin: "0 auto", padding: "44px 16px 64px" }}>

        {/* ---------- NOMOR LAIN ---------- */}
        <h2 className="font-bold leading-tight text-[var(--c-ink)]" style={{ fontSize: 26 }}>
          {pilihTeks(TEKS.nomorJudul, lang)}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 18, marginTop: 20 }}>
          {lainnya.map((n) => {
            const lewatWa = Boolean(n.wa);

            return (
              <article
                key={n.kunci}
                className="border border-[var(--c-line)] bg-[var(--c-surface)] transition-colors hover:border-[var(--c-brand-line)]"
                style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                  <span className="text-[var(--c-brand)]" style={{ display: "flex" }}>
                    {lewatWa ? <IkonWhatsApp /> : <IkonTelepon />}
                  </span>
                  <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                    {pilihTeks(n.label, lang)}
                  </p>

                  {/* Tanda hanya untuk yang mengerjakan situs, dan
                     hanya saat pengembangan. Lihat TANDAI_PERLU_DICEK
                     di atas. */}
                  {tampilkanTanda && n.perluDicek && (
                    <span className="font-bold text-amber-900" style={{ backgroundColor: "#fef3c7", borderRadius: 999, padding: "2px 8px", fontSize: 10 }}>
                      {pilihTeks(TEKS.perluDicek, lang)}
                    </span>
                  )}
                </div>

                <p style={{ marginTop: 12 }}>
                  <a
                    href={`tel:${n.tel}`}
                    className="font-bold text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]"
                    style={{ fontSize: 21 }}
                  >
                    {n.tampil}
                  </a>
                </p>

                {n.keterangan && (
                  <p className="text-[var(--c-body)]" style={{ marginTop: 9, flex: "1 1 auto", fontSize: 13.5, lineHeight: 1.6 }}>
                    {pilihTeks(n.keterangan, lang)}
                  </p>
                )}

                <a
                  href={tautanNomor(n)}
                  target={lewatWa ? "_blank" : undefined}
                  rel={lewatWa ? "noopener noreferrer" : undefined}
                  className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)]"
                  style={{ marginTop: 14, fontSize: 13.5 }}
                >
                  {lewatWa ? pilihTeks(TEKS.chatWa, lang) : pilihTeks(TEKS.telepon, lang)}
                </a>
              </article>
            );
          })}
        </div>

        {/* ---------- ALAMAT DAN SUREL ---------- */}
        <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
          {pilihTeks(TEKS.lokasiJudul, lang)}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 18, marginTop: 18 }}>
          <article className="border border-[var(--c-line)] bg-[var(--c-surface)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}>
            <div className="text-[var(--c-brand)]" style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <IkonPeta />
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                {pilihTeks(TEKS.alamat, lang)}
              </p>
            </div>

            <p className="text-slate-700" style={{ marginTop: 12, flex: "1 1 auto", fontSize: 14.5, lineHeight: 1.65 }}>
              {ALAMAT.baris}
            </p>

            <a
              href={peta}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[var(--c-brand)] underline underline-offset-4 transition-colors hover:text-[var(--c-ink)]"
              style={{ marginTop: 14, fontSize: 13.5 }}
            >
              {pilihTeks(TEKS.lihatPeta, lang)}
            </a>
          </article>

          <article className="border border-[var(--c-line)] bg-[var(--c-surface)]" style={{ display: "flex", flexDirection: "column", borderRadius: 14, padding: "20px 18px" }}>
            <div className="text-[var(--c-brand)]" style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <IkonSurel />
              <p className="font-semibold uppercase tracking-[0.12em] text-[var(--c-faint)]" style={{ fontSize: 10.5 }}>
                {pilihTeks(TEKS.surel, lang)}
              </p>
            </div>

            {/* break-all: alamat surel panjang tanpa spasi bisa
               melebar melewati kartunya di layar sempit. */}
            <p style={{ marginTop: 12, flex: "1 1 auto" }}>
              <a
                href={`mailto:${SUREL}`}
                className="font-bold text-[var(--c-ink)] transition-colors hover:text-[var(--c-brand)]"
                style={{ fontSize: 15.5, wordBreak: "break-all" }}
              >
                {SUREL}
              </a>
            </p>
          </article>
        </div>

        {/* ---------- FOTO ---------- */}
        {FOTO.length > 0 && (
          <>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ marginTop: 56, fontSize: 22 }}>
              {pilihTeks(TEKS.fotoJudul, lang)}
            </h2>
            <p className="text-[var(--c-muted)]" style={{ marginTop: 8, fontSize: 13.5 }}>
              {pilihTeks(TEKS.fotoSub, lang)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 16, marginTop: 16 }}>
              {FOTO.map((f, i) => (
                <figure key={i} style={{ margin: 0 }}>
                  <button
                    id={`foto-pelayanan-${i}`}
                    type="button"
                    onClick={() => setPratinjau(i)}
                    aria-label={pilihTeks(TEKS.perbesar, lang)}
                    className="group relative block w-full overflow-hidden border border-[var(--c-line)] transition-colors hover:border-[var(--c-brand-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)]"
                    style={{ borderRadius: 12, aspectRatio: "4 / 3", backgroundColor: "#dff2e8" }}
                  >
                    {/* aspectRatio + objectFit cover: fotonya dipotong
                       mengikuti bingkai, bukan bingkainya yang
                       mengikuti foto. Foto pertama berorientasi
                       potret, dua lainnya lanskap - tanpa ini,
                       barisnya jadi tidak rata. */}
                    <Image
                      src={f.gambar}
                      alt={pilihTeks(f.alt, lang)}
                      fill
                      placeholder="blur"
                      sizes="(min-width: 768px) 360px, 100vw"
                      className="transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transform-none"
                      style={{ objectFit: "cover", objectPosition: "center" }}
                    />
                  </button>

                  <figcaption className="font-semibold text-[var(--c-body)]" style={{ marginTop: 9, fontSize: 13.5 }}>
                    {pilihTeks(f.judul, lang)}
                  </figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </section>

      <Footer />

      {pratinjau !== null && (
        <Pratinjau
          indeks={pratinjau}
          lang={lang}
          onTutup={() => setPratinjau(null)}
          onPindah={(i) => setPratinjau(i)}
        />
      )}
    </div>
  );
}

/* ============================================================
   PRATINJAU FOTO - pola yang sama dengan halaman IGD dan
   Laboratorium.

   Ditutup dengan tombol silang, Esc, atau menekan latarnya. Gulir
   halaman dikunci selama terbuka, dan fokus papan ketik
   dikembalikan ke foto yang ditekan saat ditutup.
   ============================================================ */
function Pratinjau({ indeks, lang, onTutup, onPindah }) {
  const tombolTutup = useRef(null);
  const foto = FOTO[indeks];
  const banyak = FOTO.length > 1;

  useEffect(() => {
    tombolTutup.current?.focus();

    const gulirSemula = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const tekan = (e) => {
      if (e.key === "Escape") onTutup();
      if (!banyak) return;
      if (e.key === "ArrowRight") onPindah((indeks + 1) % FOTO.length);
      if (e.key === "ArrowLeft") onPindah((indeks - 1 + FOTO.length) % FOTO.length);
    };

    window.addEventListener("keydown", tekan);

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirSemula;
      document.getElementById(`foto-pelayanan-${indeks}`)?.focus();
    };
  }, [indeks, banyak, onTutup, onPindah]);

  if (!foto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={pilihTeks(foto.judul, lang)}
      onClick={onTutup}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(15, 4, 12, 0.86)", padding: 16 }}
    >
      {/* width: fit-content - kotaknya menyusut mengikuti gambar, jadi
         tidak ada bidang kosong di kiri-kanan. */}
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "fit-content", maxWidth: "100%" }}>
        <Image
          src={foto.gambar}
          alt={pilihTeks(foto.alt, lang)}
          sizes="90vw"
          className="rounded-lg"
          style={{ display: "block", width: "auto", height: "auto", maxWidth: "100%", maxHeight: "82vh" }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12 }}>
          <p className="font-semibold text-white/80" style={{ fontSize: 13 }}>
            {pilihTeks(foto.judul, lang)}
          </p>

          {banyak && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="text-white/60" style={{ fontSize: 12.5 }}>
                {indeks + 1} / {FOTO.length}
              </span>
              <button
                type="button"
                onClick={() => onPindah((indeks - 1 + FOTO.length) % FOTO.length)}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &larr;
              </button>
              <button
                type="button"
                onClick={() => onPindah((indeks + 1) % FOTO.length)}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="bg-white/15 font-bold text-white transition-colors hover:bg-white/25"
                style={{ borderRadius: 8, padding: "8px 14px", fontSize: 13 }}
              >
                &rarr;
              </button>
            </div>
          )}
        </div>

        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label={pilihTeks(TEKS.tutup, lang)}
          className="bg-[var(--c-surface)] text-[var(--c-ink)] shadow-lg transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          style={{ position: "absolute", top: -14, right: -6, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 999 }}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18 }}>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* Ikon ditulis sebagai komponen kecil supaya dipakai berulang tanpa
   menyalin path SVG-nya. aria-hidden karena maknanya sudah
   disampaikan tulisan di sebelahnya. */
function IkonTelepon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M4.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 2.5 5.7 2 2 0 0 1 4.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function IkonWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M3.5 20.5l1.3-4.2A8.2 8.2 0 1 1 8 19.3l-4.5 1.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M9 8.5c.3 2.5 2 4.2 4.5 4.5l.8-1.3 2 .8v1.3c0 .6-.5 1.1-1.1 1a7.8 7.8 0 0 1-7.2-7.2c0-.6.4-1.1 1-1.1h1.3l.8 2L9 8.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function IkonPeta() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function IkonSurel() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ width: 18, height: 18, flex: "0 0 auto" }}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
